import { useState } from "react";
import { Link } from "react-router-dom";
import { useTheme, stats, services, strategicPartner } from "../data/data";
import { Fade, useInView, useCounter, SectionLabel, SectionTitle, SmartImage } from "../utils/ui";

function StatCard({ value, suffix, label }) {
  const { theme } = useTheme();
  const [ref, inView] = useInView(0.3);
  const count = useCounter(value, inView);
  return (
    <div ref={ref} style={{
      textAlign: "center", padding: "1.8rem 1rem",
      background: theme.cardBg, borderRadius: 12,
      border: `1px solid ${theme.border}`,
      boxShadow: theme.shadowCard, transition: "background 0.35s, border-color 0.35s",
    }}>
      <div style={{ fontFamily: "'Playfair Display',serif", fontSize: "clamp(2rem,3.5vw,2.8rem)", fontWeight: 700, color: theme.primary, lineHeight: 1 }}>{count}{suffix}</div>
      <div style={{ fontSize: "0.72rem", letterSpacing: "0.14em", textTransform: "uppercase", color: theme.textMuted, marginTop: 8, fontWeight: 500 }}>{label}</div>
    </div>
  );
}

const HERO_IMG = "https://images.unsplash.com/photo-1494412519320-aa613dfb7738?auto=format&fit=crop&w=1600&q=80";

const categoryImages = {
  industrial: {
    img: "https://images.unsplash.com/photo-1518709268805-4e9042af2176?auto=format&fit=crop&w=900&q=80",
    seed: "industry-cat",
  },
  agro: {
    img: "https://images.unsplash.com/photo-1574323347407-f5e1ad6d020b?auto=format&fit=crop&w=900&q=80",
    seed: "agro-cat",
  },
  spices: {
    img: "https://images.unsplash.com/photo-1532336414038-cf19250c5757?auto=format&fit=crop&w=900&q=80",
    seed: "spices-cat",
  },
};

export default function Home() {
  const { theme } = useTheme();

  return (
    <div style={{ paddingTop: 70 }}>

      {/* ── HERO ── */}
      <section style={{ minHeight: "94vh", display: "flex", alignItems: "center", position: "relative", overflow: "hidden", background: theme.heroBg }}>
        {/* Background image with dark overlay */}
        <div style={{
          position: "absolute", inset: 0,
          backgroundImage: `linear-gradient(135deg, rgba(10,45,74,0.88), rgba(13,76,122,0.78)), url(${HERO_IMG})`,
          backgroundSize: "cover", backgroundPosition: "center",
          zIndex: 0,
        }} />

        {/* Subtle grid */}
        <div style={{ position: "absolute", inset: 0, backgroundImage: "linear-gradient(rgba(255,255,255,0.025) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,0.025) 1px,transparent 1px)", backgroundSize: "56px 56px", zIndex: 1 }} />

        {/* Decorative ring (reduced opacity since we now have a photo bg) */}
        <div className="hero-ring" style={{ position: "absolute", right: "4%", top: "50%", transform: "translateY(-50%)", width: 480, height: 480, zIndex: 2, opacity: 0.85 }}>
          <div style={{ position: "absolute", inset: 0, borderRadius: "50%", border: `1px solid ${theme.accent}45`, animation: "spinRing 28s linear infinite" }}>
            {[0,60,120,180,240,300].map((deg, i) => (
              <div key={i} style={{ position: "absolute", width: 10, height: 10, borderRadius: "50%", background: theme.accent, opacity: 0.85, top: "50%", left: "50%", transform: `rotate(${deg}deg) translateX(238px) translateY(-50%)` }} />
            ))}
          </div>
          <div style={{ position: "absolute", inset: 58, borderRadius: "50%", border: "1px dashed rgba(255,255,255,0.15)", animation: "spinRing 20s linear infinite reverse" }} />
          <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)", width: 200, height: 200, borderRadius: "50%", background: `${theme.accent}22`, border: `2px solid ${theme.accent}55`, display: "flex", alignItems: "center", justifyContent: "center", animation: "floatBob 5s ease-in-out infinite", boxShadow: `0 0 50px ${theme.accent}55` }}>
            <span style={{ fontSize: "5rem" }}>🌍</span>
          </div>
        </div>

        <div style={{ position: "relative", zIndex: 3, maxWidth: 700, padding: "0 5vw" }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 9, background: `${theme.accent}22`, border: `1px solid ${theme.accent}45`, borderRadius: 24, padding: "0.42rem 1.1rem", marginBottom: "2rem", animation: "fadeUp 0.7s ease 0.15s both" }}>
            <span style={{ width: 7, height: 7, borderRadius: "50%", background: theme.accent, display: "inline-block", animation: "floatBob 2s ease-in-out infinite" }} />
            <span style={{ fontSize: "0.7rem", letterSpacing: "0.18em", textTransform: "uppercase", color: theme.accent, fontWeight: 600 }}>Global Commodity & Agro Trade Partner</span>
          </div>

          <h1 style={{ fontFamily: "'Playfair Display',serif", fontSize: "clamp(2.8rem,5.5vw,5rem)", fontWeight: 700, lineHeight: 1.08, color: theme.textOnHero, marginBottom: "1.4rem", animation: "fadeUp 0.8s ease 0.3s both" }}>
            Trusted Supply.<br />
            <span style={{ color: theme.accent }}>Global</span> Reach.
          </h1>

          <p style={{ fontSize: "1.05rem", lineHeight: 1.82, color: theme.textOnHeroMuted, maxWidth: 510, marginBottom: "2.5rem", animation: "fadeUp 0.8s ease 0.45s both" }}>
            Kuldiip Enterprises delivers premium industrial raw materials and agro commodities to international markets — competitive pricing, certified quality, reliable logistics.
          </p>

          <div style={{ display: "flex", gap: 14, flexWrap: "wrap", animation: "fadeUp 0.8s ease 0.6s both" }}>
            <Link to="/products" style={{ background: theme.accent, color: theme.textOnAccent, textDecoration: "none", padding: "0.9rem 1.9rem", borderRadius: 8, fontSize: "0.9rem", fontWeight: 700, boxShadow: `0 4px 22px ${theme.accent}50`, transition: "all 0.25s", display: "inline-flex", alignItems: "center", gap: 8 }}
              onMouseEnter={e => { e.currentTarget.style.background = theme.accentHover; e.currentTarget.style.transform = "translateY(-3px)"; }}
              onMouseLeave={e => { e.currentTarget.style.background = theme.accent; e.currentTarget.style.transform = "translateY(0)"; }}>
              Explore Products <span>→</span>
            </Link>
            <Link to="/contact" style={{ background: "rgba(255,255,255,0.1)", color: theme.textOnHero, textDecoration: "none", padding: "0.9rem 1.9rem", borderRadius: 8, fontSize: "0.9rem", fontWeight: 600, border: "1px solid rgba(255,255,255,0.32)", transition: "all 0.25s", display: "inline-flex", alignItems: "center", gap: 8 }}
              onMouseEnter={e => { e.currentTarget.style.background = "rgba(255,255,255,0.18)"; e.currentTarget.style.transform = "translateY(-3px)"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,0.1)"; e.currentTarget.style.transform = "translateY(0)"; }}>
              Request a Quote
            </Link>
          </div>

          {/* Scroll hint */}
          <div style={{ marginTop: "4rem", display: "flex", alignItems: "center", gap: 10, animation: "fadeUp 0.8s ease 0.9s both" }}>
            <div style={{ width: 28, height: 28, border: `1.5px solid rgba(255,255,255,0.3)`, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <span style={{ fontSize: "0.65rem", color: "rgba(255,255,255,0.5)" }}>↓</span>
            </div>
            <span style={{ fontSize: "0.7rem", letterSpacing: "0.14em", textTransform: "uppercase", color: "rgba(255,255,255,0.4)" }}>Scroll to explore</span>
          </div>
        </div>
      </section>

      {/* ── STATS ── */}
      <section style={{ padding: "4.5rem 5vw", background: theme.cardBg, borderBottom: `1px solid ${theme.border}`, transition: "background 0.35s" }}>
        <div style={{ maxWidth: 1000, margin: "0 auto", display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: "1.4rem" }} className="grid-4">
          {stats.map(s => <StatCard key={s.label} {...s} />)}
        </div>
      </section>

      {/* ── CATEGORIES (with photos) ── */}
      <section style={{ padding: "6rem 5vw", background: theme.pageBg, transition: "background 0.35s" }}>
        <div style={{ maxWidth: 1280, margin: "0 auto" }}>
          <Fade style={{ textAlign: "center", marginBottom: "3.5rem" }}>
            <SectionLabel theme={theme}>What We Offer</SectionLabel>
            <SectionTitle theme={theme} center>Our Product Categories</SectionTitle>
            <p style={{ fontSize: "0.95rem", color: theme.textSecondary, maxWidth: 520, margin: "0 auto", lineHeight: 1.82 }}>
              Three major export categories covering industrial minerals and premium agro commodities for global trade.
            </p>
          </Fade>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: "1.8rem" }} className="grid-3">
            {[
              { key:"industrial", icon:"⚙️", title:"Industrial Raw Materials", desc:"Iron Ore, Sponge Iron, Iron Pellets, Coal, Bauxite, Quartz and other minerals for global manufacturing.", count:"6 Products", color:"#1d6fa8" },
              { key:"agro",       icon:"🌾", title:"Agro Commodities",         desc:"IR64 Parboiled Rice, IR64 White Rice, and Indian Sugar (M30 & S30) of premium export quality standards.", count:"3 Products", color:"#059669" },
              { key:"spices",     icon:"🌶️", title:"Export Quality Spices",    desc:"Turmeric, Red Chilli, Cumin, Coriander, Black Pepper, Cardamom and Cloves sourced from Rajasthan.", count:"3 Products", color:"#dc2626" },
            ].map((cat, i) => (
              <Fade key={i} delay={i * 0.14}>
                <CategoryCard cat={cat} />
              </Fade>
            ))}
          </div>
        </div>
      </section>

      {/* ── WHAT WE DO / SERVICES (from S&R PDF) ── */}
      <section style={{ padding: "6rem 5vw", background: theme.cardBg, borderTop: `1px solid ${theme.border}`, transition: "background 0.35s" }}>
        <div style={{ maxWidth: 1280, margin: "0 auto" }}>
          <Fade style={{ textAlign: "center", marginBottom: "3.5rem" }}>
            <SectionLabel theme={theme}>What We Do</SectionLabel>
            <SectionTitle theme={theme} center>End-to-End Trade Services</SectionTitle>
            <p style={{ fontSize: "0.95rem", color: theme.textSecondary, maxWidth: 600, margin: "0 auto", lineHeight: 1.82 }}>
              We act as the bridge between buyers and sellers — sourcing, mediating, shipping, and advising on every transaction.
            </p>
          </Fade>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(2,1fr)", gap: "1.5rem" }} className="grid-2">
            {services.map((s, i) => (
              <Fade key={i} delay={i * 0.1}>
                <ServiceCard s={s} theme={theme} />
              </Fade>
            ))}
          </div>
        </div>
      </section>

      {/* ── WHY US ── */}
      <section style={{ padding: "6rem 5vw", background: theme.primary, transition: "background 0.35s" }}>
        <div style={{ maxWidth: 1280, margin: "0 auto" }}>
          <Fade style={{ textAlign: "center", marginBottom: "3.5rem" }}>
            <div style={{ display: "inline-block", fontSize: "0.7rem", letterSpacing: "0.2em", textTransform: "uppercase", color: theme.accent, fontWeight: 700, background: `${theme.accent}22`, padding: "0.3rem 0.85rem", borderRadius: 20, marginBottom: 14 }}>Why Choose Us</div>
            <h2 style={{ fontFamily: "'Playfair Display',serif", fontSize: "clamp(1.8rem,3vw,2.5rem)", fontWeight: 700, color: "#fff" }}>Quality You Can Trust</h2>
          </Fade>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: "1.8rem" }} className="grid-3">
            {[
              { icon:"🏆", title:"High Quality", desc:"Standardized products with rigorous quality checks meeting all international import standards. Every shipment certified." },
              { icon:"💰", title:"Competitive Pricing", desc:"Direct sourcing network enables the best bulk pricing globally. No hidden charges — transparent quotes every time." },
              { icon:"🚢", title:"Timely Delivery", desc:"End-to-end logistics from inland sourcing to international port delivery. Real-time tracking on all consignments." },
            ].map((item, i) => (
              <Fade key={i} delay={i * 0.14}>
                <div style={{ background: "rgba(255,255,255,0.07)", border: "1px solid rgba(255,255,255,0.14)", borderRadius: 14, padding: "2.2rem", transition: "all 0.3s" }}
                  onMouseEnter={e => { e.currentTarget.style.background = "rgba(255,255,255,0.12)"; e.currentTarget.style.transform = "translateY(-4px)"; }}
                  onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,0.07)"; e.currentTarget.style.transform = "translateY(0)"; }}>
                  <div style={{ fontSize: "2.8rem", marginBottom: "1.1rem" }}>{item.icon}</div>
                  <h3 style={{ fontSize: "1.1rem", fontWeight: 700, color: "#fff", marginBottom: "0.7rem" }}>{item.title}</h3>
                  <p style={{ fontSize: "0.86rem", color: "rgba(255,255,255,0.68)", lineHeight: 1.8 }}>{item.desc}</p>
                </div>
              </Fade>
            ))}
          </div>
        </div>
      </section>

      {/* ── STRATEGIC PARTNER (S&R Odyssey) ── */}
      <section style={{ padding: "6rem 5vw", background: theme.pageBg, transition: "background 0.35s" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <Fade style={{ textAlign: "center", marginBottom: "3rem" }}>
            <SectionLabel theme={theme}>Our Strategic Partner</SectionLabel>
            <SectionTitle theme={theme} center>Connected to North America via {strategicPartner.name}</SectionTitle>
          </Fade>

          <Fade>
            <div style={{
              background: theme.cardBg,
              border: `1px solid ${theme.border}`,
              borderRadius: 18,
              boxShadow: theme.shadowCard,
              overflow: "hidden",
              display: "grid",
              gridTemplateColumns: "1fr 1.4fr",
              transition: "background 0.35s",
            }} className="grid-2">
              <div style={{ position: "relative", minHeight: 320, background: "linear-gradient(135deg,#0a2d4a,#1565a0)" }}>
                <SmartImage
                  src="https://images.unsplash.com/photo-1518391846015-55a9cc003b25?auto=format&fit=crop&w=900&q=80"
                  fallbackSeed="canada-skyline"
                  alt="North America"
                  style={{ opacity: 0.55 }}
                />
                <div style={{ position: "absolute", inset: 0, padding: "2rem", display: "flex", flexDirection: "column", justifyContent: "flex-end", color: "#fff" }}>
                  <div style={{ fontSize: "0.7rem", letterSpacing: "0.18em", textTransform: "uppercase", opacity: 0.85, marginBottom: 8 }}>🇨🇦 {strategicPartner.country}</div>
                  <div style={{ fontFamily: "'Playfair Display',serif", fontSize: "1.6rem", fontWeight: 700, lineHeight: 1.2 }}>{strategicPartner.name}</div>
                  <div style={{ fontSize: "0.85rem", opacity: 0.85, marginTop: 4 }}>{strategicPartner.tagline}</div>
                </div>
              </div>

              <div style={{ padding: "2.2rem 2.4rem" }}>
                <div style={{ fontSize: "0.7rem", letterSpacing: "0.16em", textTransform: "uppercase", color: theme.accent, fontWeight: 700, marginBottom: 10 }}>{strategicPartner.role}</div>
                <p style={{ fontSize: "0.92rem", color: theme.textSecondary, lineHeight: 1.85, marginBottom: "1.4rem" }}>
                  {strategicPartner.desc}
                </p>
                <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 8 }}>
                  {strategicPartner.highlights.map((h, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 10, fontSize: "0.85rem", color: theme.textSecondary }}>
                      <span style={{ color: theme.accent, fontWeight: 700, flexShrink: 0 }}>✓</span>
                      <span>{h}</span>
                    </div>
                  ))}
                </div>
                <div style={{ marginTop: 18, fontSize: "0.82rem", color: theme.textMuted }}>
                  🌐 <span style={{ color: theme.primary, fontWeight: 600 }}>{strategicPartner.website}</span>
                </div>
              </div>
            </div>
          </Fade>
        </div>
      </section>

      {/* ── CTA ── */}
      <section style={{ padding: "6rem 5vw", background: theme.sectionAlt, textAlign: "center", transition: "background 0.35s" }}>
        <Fade>
          <div style={{ background: theme.cardBg, border: `1px solid ${theme.border}`, borderRadius: 20, padding: "4rem 3rem", maxWidth: 720, margin: "0 auto", boxShadow: theme.shadowHover, transition: "background 0.35s" }}>
            <div style={{ display: "inline-block", background: `${theme.accent}18`, borderRadius: 20, padding: "0.35rem 1rem", fontSize: "0.7rem", letterSpacing: "0.18em", textTransform: "uppercase", color: theme.accent, fontWeight: 700, marginBottom: 18 }}>Start Today</div>
            <h2 style={{ fontFamily: "'Playfair Display',serif", fontSize: "clamp(1.8rem,3vw,2.5rem)", fontWeight: 700, color: theme.textPrimary, marginBottom: "1rem", lineHeight: 1.2 }}>Ready to Start a Global Partnership?</h2>
            <p style={{ fontSize: "1rem", color: theme.textSecondary, marginBottom: "2.2rem", lineHeight: 1.8, maxWidth: 480, margin: "0 auto 2.2rem" }}>
              Open for bulk orders and long-term trade partnerships worldwide. Contact us today for pricing.
            </p>
            <div style={{ display: "flex", gap: 14, justifyContent: "center", flexWrap: "wrap" }}>
              <Link to="/contact" style={{ background: theme.accent, color: theme.textOnAccent, textDecoration: "none", padding: "0.9rem 2rem", borderRadius: 8, fontSize: "0.9rem", fontWeight: 700, boxShadow: `0 4px 18px ${theme.accent}40`, transition: "all 0.25s" }}
                onMouseEnter={e => { e.currentTarget.style.background = theme.accentHover; e.currentTarget.style.transform = "translateY(-3px)"; }}
                onMouseLeave={e => { e.currentTarget.style.background = theme.accent; e.currentTarget.style.transform = "translateY(0)"; }}>
                Contact Us Now →
              </Link>
              <Link to="/products" style={{ background: "transparent", color: theme.primary, textDecoration: "none", padding: "0.9rem 2rem", borderRadius: 8, fontSize: "0.9rem", fontWeight: 600, border: `1.5px solid ${theme.border}`, transition: "all 0.25s" }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = theme.primary; e.currentTarget.style.transform = "translateY(-3px)"; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = theme.border; e.currentTarget.style.transform = "translateY(0)"; }}>
                View Products
              </Link>
            </div>
          </div>
        </Fade>
      </section>
    </div>
  );
}

/* ── Sub-components ── */

function CategoryCard({ cat }) {
  const { theme } = useTheme();
  const imgRef = categoryImages[cat.key];
  const [hov, setHov] = useState(false);

  return (
    <Link
      to="/products"
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        background: theme.cardBg,
        borderRadius: 16,
        overflow: "hidden",
        border: `1px solid ${hov ? cat.color + "60" : theme.border}`,
        boxShadow: hov ? theme.shadowHover : theme.shadowCard,
        transform: hov ? "translateY(-6px)" : "translateY(0)",
        transition: "all 0.3s ease, background 0.35s",
        textDecoration: "none",
        display: "block",
      }}
    >
      <div style={{ position: "relative", height: 180, overflow: "hidden" }}>
        <div style={{
          width: "100%", height: "100%",
          transform: hov ? "scale(1.1)" : "scale(1)",
          transition: "transform 0.6s cubic-bezier(0.22,1,0.36,1)",
        }}>
          <SmartImage src={imgRef.img} fallbackSeed={imgRef.seed} alt={cat.title} />
        </div>
        <div style={{
          position: "absolute", inset: 0,
          background: `linear-gradient(180deg, ${cat.color}55 0%, ${cat.color}aa 100%)`,
        }} />
        <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 5, background: cat.color }} />
        <div style={{
          position: "absolute", inset: 0, padding: "1.4rem",
          display: "flex", flexDirection: "column", justifyContent: "space-between",
        }}>
          <div style={{ fontSize: "3rem" }}>{cat.icon}</div>
          <div style={{ display: "inline-block", alignSelf: "flex-start", fontSize: "0.65rem", letterSpacing: "0.13em", textTransform: "uppercase", background: "rgba(255,255,255,0.95)", color: cat.color, padding: "0.22rem 0.75rem", borderRadius: 20, fontWeight: 700 }}>
            {cat.count}
          </div>
        </div>
      </div>
      <div style={{ padding: "1.6rem 1.8rem 1.8rem" }}>
        <h3 style={{ fontFamily: "'Playfair Display',serif", fontSize: "1.25rem", fontWeight: 700, color: theme.textPrimary, marginBottom: "0.7rem", lineHeight: 1.3 }}>{cat.title}</h3>
        <p style={{ fontSize: "0.86rem", color: theme.textSecondary, lineHeight: 1.8, marginBottom: "1.2rem" }}>{cat.desc}</p>
        <span style={{ fontSize: "0.82rem", fontWeight: 700, color: cat.color, display: "inline-flex", alignItems: "center", gap: hov ? 10 : 6, transition: "gap 0.2s" }}>
          View All Products →
        </span>
      </div>
    </Link>
  );
}

function ServiceCard({ s, theme }) {
  const [hov, setHov] = useState(false);
  return (
    <div
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        background: theme.cardBg,
        border: `1px solid ${hov ? theme.borderHover : theme.border}`,
        borderRadius: 16,
        overflow: "hidden",
        boxShadow: hov ? theme.shadowHover : theme.shadowCard,
        transform: hov ? "translateY(-4px)" : "translateY(0)",
        transition: "all 0.3s ease, background 0.35s",
        display: "grid",
        gridTemplateColumns: "150px 1fr",
        minHeight: 180,
      }}
    >
      <div style={{ position: "relative", overflow: "hidden", background: theme.sectionAlt }}>
        <div style={{ width: "100%", height: "100%", transform: hov ? "scale(1.12)" : "scale(1)", transition: "transform 0.6s cubic-bezier(0.22,1,0.36,1)" }}>
          <SmartImage src={s.image} fallbackSeed={s.fallbackSeed} alt={s.title} />
        </div>
        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(135deg, rgba(0,0,0,0.15), rgba(0,0,0,0.45))" }} />
        <div style={{ position: "absolute", bottom: 12, left: 12, fontSize: "1.8rem" }}>{s.icon}</div>
      </div>
      <div style={{ padding: "1.4rem 1.6rem" }}>
        <h3 style={{ fontFamily: "'Playfair Display',serif", fontSize: "1.18rem", fontWeight: 700, color: theme.textPrimary, marginBottom: "0.6rem", lineHeight: 1.3 }}>{s.title}</h3>
        <p style={{ fontSize: "0.85rem", color: theme.textSecondary, lineHeight: 1.8 }}>{s.desc}</p>
      </div>
    </div>
  );
}
